import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../store/user/user.selector';
import {
    listenToWhatnotCards,
    removeCardFromWhatnot,
    createWhatnotLot,
    ungroupWhatnotLot,
    addCustomCardToWhatnot,
    updateWhatnotItem,
} from '../../../utils/firebase/whatnot';
import { ensureEbaySkus } from '../../../utils/firebase/ebay';
import { reassignStagedItems } from '../../../utils/firebase/stagedQueue';
import { listenToWhatnotShows, createWhatnotShow, addCardToShow } from '../../../utils/firebase/whatnotShow';
import useTransactionsReady from '../../../hooks/useTransactionsReady';
import {
    buildTitle,
    buildSingleDescription,
    buildLotDescription,
    isLotItem,
    isSingleItem,
} from '../../../utils/whatnot/whatnotListingText';
import { itemMatchesPlayerSearch } from '../../../utils/ebay/ebayQueueSearch';
import { sumStoredListPrices } from '../../../utils/ebay/ebayLotPrice';
import { useWhatnotPrefs } from '../../../hooks/useWhatnotPrefs';
import MultiResponseInput from '../../../components/inputs/multi-response-input/MultiResponseInput';
import { IconPlus } from '@tabler/icons-react';

import {
    WhatnotPageContainer,
    ShowsContainer,
    ShowRail,
    NewShowBtn,
    ShowCard,
    ShowCardTitle,
    ShowCardDate,
    ItemCount,
    EmptyState,
    LoadingState,
    ConfigPanel,
    ConfigFieldsContainer,
    ConfigActions,
    ExportBtn,
    AddCardBtn,
    SelectAllContainer,
    MiddleContent,
    RightContent,
    QueueControls,
    QueueHeader,
    QueueHeaderActions,
    SelectionCount,
    ShowSelectDropdown,
    AddToShowActionBtn,
    ModalOverlay,
    ModalCard,
    ModalActions,
    ModalCancelBtn,
    ModalCreateBtn,
    CombineLotBtn,
    DescriptionPreview,
    DescriptionPreviewLabel,
    ModalFieldsGrid,
} from './WhatnotPage.styles';

// components
import ListingPanel from './components/listing-panel/ListingPanel';
import InputBar from '../../../components/input-bar/InputBar';
import CardAttribute from '../../../components/card-attribute/CardAttribute';

/* ─── CSV helpers ─────────────────────────────────────────────────────────── */

const CSV_HEADERS = [
    'Category', 'Sub Category', 'Title', 'Description',
    'Quantity', 'Type', 'Price', 'Shipping Profile',
    'Offerable', 'Hazmat', 'Condition', 'Cost Per Item', 'SKU',
    'Image URL 1', 'Image URL 2', 'Image URL 3', 'Image URL 4',
    'Image URL 5', 'Image URL 6', 'Image URL 7', 'Image URL 8',
];

function csvEscape(value) {
    const str = value == null ? '' : String(value);
    return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
}

function buildListingRow(item, fallbackPrefs) {
    const category        = item.category        ?? fallbackPrefs.category;
    const subCategory     = item.subCategory     ?? fallbackPrefs.subCategory;
    const shippingProfile = item.shippingProfile ?? fallbackPrefs.shippingProfile;

    if (isLotItem(item)) {
        return {
            category, subCategory, shippingProfile,
            title:       item.title ?? 'Untitled Lot',
            description: item.description ?? buildLotDescription(item.cards ?? []),
        };
    }
    return {
        category, subCategory, shippingProfile,
        title:       buildTitle(item),
        description: buildSingleDescription(item),
    };
}

function generateCsv(items, fallbackPrefs) {
    const rows = [CSV_HEADERS.map(csvEscape).join(',')];
    items.forEach((item) => {
        const { category, subCategory, shippingProfile, title, description } = buildListingRow(item, fallbackPrefs);
        const row = [
            category, subCategory,
            title, description,
            1, 'Auction', 1, shippingProfile,
            '', 'Not Hazmat', 'Raw - Near Mint or Better', '', item.sku ?? '',
            '', '', '', '', '', '', '', '',
        ];
        rows.push(row.map(csvEscape).join(','));
    });
    return rows.join('\n');
}

function downloadCsv(csv, filename) {
    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = Object.assign(document.createElement('a'), { href: url, download: filename });
    link.click();
    URL.revokeObjectURL(url);
}

const BLANK_CARD = { series: '', name: [], team: [], cardNumber: '', cardParallelName: '', serial: '', cardAttributes: [] };

function parseSerial(raw) {
    const str = raw.trim();
    if (!str) return { cardPrintNumber: null, cardSupply: null };
    const parts = str.split('/');
    const printNum = parts[0] ? Number(parts[0].trim()) : null;
    const supply   = parts[1] ? Number(parts[1].trim()) : null;
    return {
        cardPrintNumber: !isNaN(printNum) && printNum != null ? printNum : null,
        cardSupply:      !isNaN(supply)   && supply   != null ? supply   : null,
    };
}

/* ─── Component ───────────────────────────────────────────────────────────── */

export default function WhatnotPage({ embedded = false, playerSearch = '', onSelectionChange, onListTotalChange }) {
    const user     = useSelector(selectUser);
    const navigate = useNavigate();
    const queueReady = useTransactionsReady(embedded ? null : user?.uid);

    const {
        category, subCategory, shippingProfile,
        setCategory, setSubCategory, setShippingProfile,
    } = useWhatnotPrefs();

    const [items,        setItems]        = useState([]);
    const [isLoading,    setIsLoading]    = useState(true);
    const [shows,        setShows]        = useState([]);
    const [activeShowId, setActiveShowId] = useState(null);

    // New show modal
    const [newShowModalOpen, setNewShowModalOpen] = useState(false);
    const [newShowName,      setNewShowName]      = useState('');

    // Card multi-select
    const [selectedIds,  setSelectedIds]  = useState(new Set());
    const [targetShowId, setTargetShowId] = useState('');

    // On-the-fly add form
    const [addFormOpen,  setAddFormOpen]  = useState(false);
    const [newCard,      setNewCard]      = useState(BLANK_CARD);
    const [isAdding,     setIsAdding]     = useState(false);

    // Combine lot modal
    const [combineModalOpen,   setCombineModalOpen]   = useState(false);
    const [lotTitle,           setLotTitle]           = useState('');
    const [lotCategory,        setLotCategory]        = useState('');
    const [lotSubCategory,     setLotSubCategory]     = useState('');
    const [lotShippingProfile, setLotShippingProfile] = useState('');
    const [isCombining,        setIsCombining]        = useState(false);

    const [removePending, setRemovePending] = useState(false);

    const visibleItems = useMemo(
        () => items.filter((item) => itemMatchesPlayerSearch(item, playerSearch)),
        [items, playerSearch]
    );
    const listTotal = useMemo(() => sumStoredListPrices(items), [items]);
    const allVisibleSelected = visibleItems.length > 0 && visibleItems.every((item) => selectedIds.has(item.id));

    const selectedSingles = useMemo(
        () => items.filter((item) => selectedIds.has(item.id) && isSingleItem(item)),
        [items, selectedIds]
    );

    const combinePreviewDescription = useMemo(
        () => buildLotDescription(selectedSingles),
        [selectedSingles]
    );

    const canCombine = selectedSingles.length >= 2;

    useEffect(() => { setRemovePending(false); }, [selectedIds]);

    /* ── Data listeners ── */
    useEffect(() => {
        if (!user?.uid || (!embedded && !queueReady)) { setIsLoading(false); return; }
        setIsLoading(true);
        const unsub = listenToWhatnotCards(user.uid, (cards) => {
            setItems(cards);
            setIsLoading(false);
        });
        return () => unsub();
    }, [user?.uid, embedded, queueReady]);

    useEffect(() => {
        if (!user?.uid) return;
        const unsub = listenToWhatnotShows(user.uid, setShows);
        return () => unsub();
    }, [user?.uid]);

    /* ── Show management ── */
    const handleNewShow = useCallback(() => {
        setNewShowName('');
        setNewShowModalOpen(true);
    }, []);

    const handleConfirmNewShow = useCallback(async () => {
        if (!user?.uid || !newShowName.trim()) return;
        const showId = await createWhatnotShow(user.uid, newShowName.trim());
        setActiveShowId(showId);
        setNewShowModalOpen(false);
        navigate(`/show/${showId}/host`);
    }, [user?.uid, newShowName, navigate]);

    const handleSelectShow = useCallback((show) => {
        setActiveShowId(show.id);
        navigate(`/show/${show.id}/host`);
    }, [navigate]);

    /* ── Card selection ── */
    const handleToggleSelect = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedIds((prev) => {
            const visibleIds = visibleItems.map((item) => item.id);
            const allChecked = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
            if (allChecked) {
                const next = new Set(prev);
                visibleIds.forEach((id) => next.delete(id));
                return next;
            }
            const next = new Set(prev);
            visibleIds.forEach((id) => next.add(id));
            return next;
        });
    }, [visibleItems]);

    useEffect(() => {
        if (!onSelectionChange) return;
        onSelectionChange({
            allSelected: allVisibleSelected,
            disabled: isLoading || visibleItems.length === 0,
            toggle: handleSelectAll,
        });
    }, [onSelectionChange, allVisibleSelected, isLoading, visibleItems.length, handleSelectAll]);

    useEffect(() => {
        onListTotalChange?.(listTotal);
    }, [onListTotalChange, listTotal]);

    const handleAddSelectedToShow = useCallback(async () => {
        if (!user?.uid || !targetShowId || selectedIds.size === 0) return;
        const selected = items.filter((i) => selectedIds.has(i.id) && isSingleItem(i));
        if (selected.length === 0) return;
        await Promise.all(selected.map((card) => addCardToShow(user.uid, targetShowId, card)));
        setSelectedIds(new Set());
    }, [user?.uid, targetShowId, selectedIds, items]);

    /* ── On-the-fly add ── */
    const handleOpenAddForm = useCallback(() => {
        setNewCard(BLANK_CARD);
        setAddFormOpen(true);
    }, []);

    const handleConfirmAdd = useCallback(async () => {
        if (!user?.uid || newCard.name.length === 0 || isAdding) return;
        setIsAdding(true);
        try {
            const cardFields = {
                series:           newCard.series.trim()           || null,
                name:             newCard.name,
                team:             newCard.team,
                cardNumber:       newCard.cardNumber.trim()       || null,
                cardParallelName: newCard.cardParallelName.trim() || null,
                cardAttributes:   newCard.cardAttributes,
                ...parseSerial(newCard.serial),
            };
            await addCustomCardToWhatnot(user.uid, cardFields, { category, subCategory, shippingProfile });
            setAddFormOpen(false);
            setNewCard(BLANK_CARD);
        } finally {
            setIsAdding(false);
        }
    }, [user?.uid, newCard, isAdding, category, subCategory, shippingProfile]);

    /* ── Combine lot ── */
    const handleOpenCombineModal = useCallback(() => {
        if (!canCombine) return;
        setLotTitle('');
        setLotCategory(category);
        setLotSubCategory(subCategory);
        setLotShippingProfile(shippingProfile);
        setCombineModalOpen(true);
    }, [canCombine, category, subCategory, shippingProfile]);

    const handleConfirmCombine = useCallback(async () => {
        if (!user?.uid || !lotTitle.trim() || selectedSingles.length < 2 || isCombining) return;
        setIsCombining(true);
        try {
            await createWhatnotLot(
                user.uid,
                lotTitle.trim(),
                selectedSingles.map((item) => item.id),
                selectedSingles,
                { category: lotCategory, subCategory: lotSubCategory, shippingProfile: lotShippingProfile }
            );
            setCombineModalOpen(false);
            setLotTitle('');
            setSelectedIds(new Set());
        } finally {
            setIsCombining(false);
        }
    }, [user?.uid, lotTitle, selectedSingles, isCombining, lotCategory, lotSubCategory, lotShippingProfile]);

    const handleUngroup = useCallback(async (lotItem) => {
        if (!user?.uid || !isLotItem(lotItem)) return;
        await ungroupWhatnotLot(user.uid, lotItem);
        setSelectedIds((prev) => {
            if (!prev.has(lotItem.id)) return prev;
            const next = new Set(prev);
            next.delete(lotItem.id);
            return next;
        });
    }, [user?.uid]);

    /* ── Queue actions ── */
    const handleRemove = useCallback(async (docId) => {
        if (!user?.uid) return;
        await removeCardFromWhatnot(user.uid, docId);
    }, [user?.uid]);

    const handleUpdateShipping = useCallback(async (docId, value) => {
        if (!user?.uid) return;
        await updateWhatnotItem(user.uid, docId, { shippingProfile: value });
    }, [user?.uid]);

    const handleExport = useCallback(async () => {
        const assigned = user?.uid ? await ensureEbaySkus(user.uid, items) : {};
        const withSkus = items.map((item) => (
            assigned[item.id] ? { ...item, sku: assigned[item.id] } : item
        ));
        const csv  = generateCsv(withSkus, { category, subCategory, shippingProfile });
        const date = new Date().toISOString().slice(0, 10);
        downloadCsv(csv, `whatnot-${date}.csv`);
    }, [items, category, subCategory, shippingProfile, user?.uid]);

    const handleMovePlatform = useCallback(async (platform) => {
        if (!user?.uid || selectedIds.size === 0) return;
        const selected = items.filter((item) => selectedIds.has(item.id));
        await reassignStagedItems(user.uid, selected, platform);
        setSelectedIds(new Set());
    }, [user?.uid, selectedIds, items]);

    const handleRemoveSelected = useCallback(async () => {
        if (!user?.uid || selectedIds.size === 0) return;
        if (!removePending) { setRemovePending(true); return; }
        setRemovePending(false);
        await Promise.all([...selectedIds].map((id) => removeCardFromWhatnot(user.uid, id)));
        setSelectedIds(new Set());
    }, [removePending, selectedIds, user?.uid]);

    /* ── Render ── */
    if (!embedded && !queueReady) {
        return (
            <WhatnotPageContainer>
                <LoadingState>Loading...</LoadingState>
            </WhatnotPageContainer>
        );
    }

    return (
        <WhatnotPageContainer>

            {/* Shows rail stays on the Whatnot workspace. The queue tab embeds this page without it. */}
            {!embedded && (
            <ShowsContainer>
                <ShowRail>
                    <NewShowBtn onClick={handleNewShow}>
                        <IconPlus />
                        New Show
                    </NewShowBtn>
                    {shows.map((show) => {
                        const showDate = show.showDate
                            ? new Date(show.showDate).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                            : null;
                        return (
                            <ShowCard key={show.id} $active={show.id === activeShowId} onClick={() => handleSelectShow(show)}>
                                <ShowCardTitle>{show.name ?? 'Untitled Show'}</ShowCardTitle>
                                {showDate ? <ShowCardDate>{showDate}</ShowCardDate> : <ShowCardDate>No date set.</ShowCardDate>}
                            </ShowCard>
                        );
                    })}
                </ShowRail>
            </ShowsContainer>
            )}

            {/* Config panel — also sets defaults for new items */}
            <ConfigPanel>
                <ConfigFieldsContainer>
                    <InputBar id="wn-category"    type="text" label="Category"         value={category}        updateValue={setCategory}        pH="e.g. Sports Cards" />
                    <InputBar id="wn-subcategory" type="text" label="Sub Category"     value={subCategory}     updateValue={setSubCategory}     pH="e.g. Baseball Singles" />
                    <InputBar id="wn-shipping"    type="text" label="Shipping Profile" value={shippingProfile} updateValue={setShippingProfile} pH="e.g. 1-3 oz" />
                </ConfigFieldsContainer>
                <ConfigActions>
                    <AddCardBtn onClick={handleOpenAddForm}>
                        <IconPlus size={14} />
                        Add Card
                    </AddCardBtn>
                </ConfigActions>
            </ConfigPanel>

            {isLoading && <LoadingState>Loading...</LoadingState>}

            {!isLoading && items.length === 0 && (
                <EmptyState>
                    No listings staged yet
                    <p>Add cards from the checklist, or use "Add Card" above.</p>
                </EmptyState>
            )}

            {!isLoading && items.length > 0 && (
                <>
                    <QueueHeader>
                        {!embedded && (
                            <SelectAllContainer>
                                <input
                                    type="checkbox"
                                    id="select-all"
                                    checked={allVisibleSelected}
                                    onChange={handleSelectAll}
                                    style={{ accentColor: 'rgba(255,255,255,0.85)', cursor: 'pointer', height: 15, width: 15 }}
                                />
                                <SelectionCount as="label" htmlFor="select-all" style={{ cursor: 'pointer' }}>
                                    {allVisibleSelected ? 'Deselect all' : 'Select all'}
                                </SelectionCount>
                            </SelectAllContainer>
                        )}
                        <QueueHeaderActions>
                            {selectedIds.size > 0 && (
                                <>
                                    <ShowSelectDropdown
                                        value=""
                                        onChange={(e) => { if (e.target.value) handleMovePlatform(e.target.value); }}
                                        title="Move the selected rows to another platform"
                                    >
                                        <option value="">Move to…</option>
                                        <option value="eBay">eBay</option>
                                        <option value="Discord">Discord</option>
                                    </ShowSelectDropdown>
                                    <CombineLotBtn
                                        onClick={handleOpenCombineModal}
                                        disabled={!canCombine}
                                        title={canCombine ? 'Combine selected singles into a lot' : 'Select 2 or more singles to combine'}
                                    >
                                        Create Lot
                                    </CombineLotBtn>
                                </>
                            )}
                            <ExportBtn onClick={handleExport} disabled={items.length === 0}>
                                Export CSV {!isLoading && (
                                    <ItemCount>{items.length} {items.length === 1 ? 'listing' : 'listings'}</ItemCount>
                                )}
                            </ExportBtn>
                        </QueueHeaderActions>
                    </QueueHeader>

                    {selectedIds.size > 0 && (
                        <QueueControls $expanded>
                            <MiddleContent>
                                <ShowSelectDropdown value={targetShowId} onChange={(e) => setTargetShowId(e.target.value)}>
                                    <option value="">— Select a show —</option>
                                    {shows.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name ?? 'Untitled Show'}</option>
                                    ))}
                                </ShowSelectDropdown>
                                <AddToShowActionBtn onClick={handleAddSelectedToShow} disabled={!targetShowId}>
                                    Add to Show
                                </AddToShowActionBtn>
                            </MiddleContent>
                            <RightContent>
                                <CardAttribute
                                    text={removePending ? 'CONFIRM REMOVAL?' : items.every((i) => selectedIds.has(i.id)) ? 'REMOVE ALL' : 'REMOVE SELECTED'}
                                    color="#ef4444"
                                    onClick={handleRemoveSelected}
                                />
                            </RightContent>
                        </QueueControls>
                    )}

                    {visibleItems.length === 0 ? (
                        <EmptyState>
                            No listings match "{playerSearch.trim()}"
                        </EmptyState>
                    ) : (
                    <ListingPanel
                        items={visibleItems}
                        onRemove={handleRemove}
                        onUngroup={handleUngroup}
                        onUpdateShipping={handleUpdateShipping}
                        selectedIds={selectedIds}
                        onToggleSelect={handleToggleSelect}
                    />
                    )}
                </>
            )}

            {/* Add card modal */}
            {addFormOpen && (
                <ModalOverlay onClick={() => !isAdding && setAddFormOpen(false)}>
                    <ModalCard $wide onClick={(e) => e.stopPropagation()}>
                        <h2>Add Card to Whatnot Queue</h2>
                        <InputBar id="nc-series" type="text" label="Series" value={newCard.series} updateValue={(v) => setNewCard((c) => ({ ...c, series: v }))} pH="e.g. 2024 Topps Chrome" autoFocus />
                        <ModalFieldsGrid $cols={2}>
                            <MultiResponseInput id="nc-name" label="Player(s)" pH="Add name, press Enter" values={newCard.name} onChange={(v) => setNewCard((c) => ({ ...c, name: v }))} />
                            <MultiResponseInput id="nc-team" label="Team(s)" pH="Add team, press Enter" values={newCard.team} onChange={(v) => setNewCard((c) => ({ ...c, team: v }))} />
                        </ModalFieldsGrid>
                        <ModalFieldsGrid>
                            <InputBar id="nc-num"      type="text" label="Card #"   value={newCard.cardNumber}       updateValue={(v) => setNewCard((c) => ({ ...c, cardNumber: v }))}       pH="e.g. 42" />
                            <InputBar id="nc-parallel" type="text" label="Parallel" value={newCard.cardParallelName} updateValue={(v) => setNewCard((c) => ({ ...c, cardParallelName: v }))} pH="e.g. Gold Refractor" />
                            <InputBar id="nc-serial"   type="text" label="Serial #" value={newCard.serial}           updateValue={(v) => setNewCard((c) => ({ ...c, serial: v }))}           pH="e.g. 1/10" />
                        </ModalFieldsGrid>
                        <MultiResponseInput id="nc-attrs" label="Card Attributes" pH="e.g. Rookie, Auto — press Enter to add" values={newCard.cardAttributes} onChange={(v) => setNewCard((c) => ({ ...c, cardAttributes: v }))} />
                        <ModalActions>
                            <ModalCancelBtn onClick={() => setAddFormOpen(false)} disabled={isAdding}>Cancel</ModalCancelBtn>
                            <ModalCreateBtn onClick={handleConfirmAdd} disabled={newCard.name.length === 0 || isAdding}>
                                {isAdding ? 'Adding...' : 'Add to Queue'}
                            </ModalCreateBtn>
                        </ModalActions>
                    </ModalCard>
                </ModalOverlay>
            )}

            {/* Combine lot modal */}
            {combineModalOpen && (
                <ModalOverlay onClick={() => !isCombining && setCombineModalOpen(false)}>
                    <ModalCard $wide onClick={(e) => e.stopPropagation()}>
                        <h1>Create Lot</h1>
                        <InputBar id="lot-title" type="text" label="Lot Title" value={lotTitle} updateValue={setLotTitle} pH="Enter lot title" autoFocus />
                        <ModalFieldsGrid>
                            <InputBar id="lot-cat"      type="text" label="Category"         value={lotCategory}        updateValue={setLotCategory}        pH="e.g. Sports Cards" />
                            <InputBar id="lot-subcat"   type="text" label="Sub Category"     value={lotSubCategory}     updateValue={setLotSubCategory}     pH="e.g. Baseball Lots" />
                            <InputBar id="lot-shipping" type="text" label="Shipping Profile" value={lotShippingProfile} updateValue={setLotShippingProfile} pH="e.g. 4-7 oz" />
                        </ModalFieldsGrid>
                        <div>
                            <DescriptionPreviewLabel>Description preview</DescriptionPreviewLabel>
                            <DescriptionPreview>{combinePreviewDescription}</DescriptionPreview>
                        </div>
                        <ModalActions>
                            <ModalCancelBtn onClick={() => setCombineModalOpen(false)} disabled={isCombining}>Cancel</ModalCancelBtn>
                            <ModalCreateBtn onClick={handleConfirmCombine} disabled={!lotTitle.trim() || selectedSingles.length < 2 || isCombining}>
                                {isCombining ? 'Creating...' : 'Create Lot'}
                            </ModalCreateBtn>
                        </ModalActions>
                    </ModalCard>
                </ModalOverlay>
            )}

            {/* New show modal */}
            {newShowModalOpen && (
                <ModalOverlay onClick={() => setNewShowModalOpen(false)}>
                    <ModalCard onClick={(e) => e.stopPropagation()}>
                        <h1>New Show</h1>
                        <InputBar id="new-show-name" type="text" label="Show Name" value={newShowName} updateValue={setNewShowName} pH="e.g. Friday Night Break" onKeyDown={(e) => e.key === 'Enter' && handleConfirmNewShow()} autoFocus />
                        <ModalActions>
                            <ModalCancelBtn onClick={() => setNewShowModalOpen(false)}>Cancel</ModalCancelBtn>
                            <ModalCreateBtn onClick={handleConfirmNewShow} disabled={!newShowName.trim()}>Create Show</ModalCreateBtn>
                        </ModalActions>
                    </ModalCard>
                </ModalOverlay>
            )}

        </WhatnotPageContainer>
    );
}
